import { Software } from '@/types/software';
import { Dialog, DialogTrigger } from '@/components/ui/dialog';
import { SoftwareDetails } from '@/components/SoftwareDetails';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '@/components/ui/carousel';
import { useEffect, useState } from 'react';
import Autoplay from 'embla-carousel-autoplay';
import Fade from 'embla-carousel-fade';
import { differenceInYears } from 'date-fns';

interface AnniversaryHighlightProps {
  software: Software[];
}

export const AnniversaryHighlight = ({ software }: AnniversaryHighlightProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const calculateYearsOfService = (launchDate: string, endDate: string) => {
    try {
      const years = differenceInYears(new Date(endDate), new Date(launchDate));
      return years;
    } catch {
      return 0;
    }
  };

  // Default placeholder when no software anniversaries are available
  if (software.length === 0) {
    return (
      <div className="absolute inset-x-0 bottom-4 z-10 px-2 sm:bottom-8 sm:px-4 md:bottom-8 lg:-bottom-12">
        <div className="container mx-auto">
          <div className="mx-auto max-w-2xl sm:max-w-3xl md:max-w-4xl">
            <div className="border-cyber-glow shadow-cyber bg-gradient-card relative overflow-hidden rounded-lg border p-3 sm:rounded-xl sm:border-2 sm:p-6 md:rounded-2xl md:p-8 lg:p-12">
              <div className="bg-cyber-glow/10 animate-pulse-glow absolute top-0 right-0 h-32 w-32 rounded-full blur-3xl sm:h-48 sm:w-48 md:h-64 md:w-64" />
              <div className="relative z-10">
                <div className="mb-3 flex items-center justify-center gap-1 sm:mb-4 sm:gap-2 md:mb-6 md:gap-3">
                  <div className="text-lg sm:text-2xl md:text-4xl">🕯️</div>
                  <h2 className="text-center text-lg font-bold sm:text-2xl md:text-3xl lg:text-4xl">
                    <span className="text-cyber-glow animate-cyber-glow">Today's Memorial</span>
                  </h2>
                  <div className="text-lg sm:text-2xl md:text-4xl">🕯️</div>
                </div>
                <p className="text-memorial-muted mb-3 text-center text-sm sm:mb-6 sm:text-base md:mb-8 md:text-lg">
                  Honoring the anniversary of software that departed on this day
                </p>
                <div className="bg-memorial-bg/50 border-memorial-border flex flex-col items-center gap-3 rounded-lg border p-3 sm:gap-6 sm:rounded-xl sm:p-4 md:gap-8 md:p-6">
                  <div className="flex-shrink-0 text-3xl sm:text-5xl md:text-8xl lg:text-9xl">
                    💭
                  </div>
                  <div className="flex-1 text-center">
                    <h3 className="text-memorial-text mb-2 text-lg font-bold sm:mb-3 sm:text-xl md:text-3xl lg:text-4xl">
                      No Anniversaries Today
                    </h3>
                    <p className="text-memorial-muted mb-2 text-xs sm:mb-3 sm:text-sm md:mb-4 md:text-base lg:text-lg">
                      The digital graveyard rests peacefully today. No software ended its journey on
                      this date in history.
                    </p>
                    <p className="text-memorial-muted mb-2 text-xs sm:mb-3 sm:text-sm md:mb-4 md:text-base lg:text-lg">
                      Do you know of a software that met its end on this day?{' '}
                      <span className="text-cyber-glow">
                        Feel free to add one to our memorial archive.
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-x-0 bottom-4 z-10 px-2 sm:bottom-8 sm:px-4 md:bottom-8 lg:-bottom-12">
      <div className="container mx-auto">
        <div className="mx-auto max-w-2xl sm:max-w-3xl md:max-w-4xl">
          <Carousel
            setApi={setApi}
            opts={{
              loop: true,
            }}
            plugins={[
              Autoplay({
                delay: 4000,
              }),
              Fade(),
            ]}
          >
            <CarouselContent>
              {software.map((item) => (
                <CarouselItem key={item.id}>
                  <Dialog>
                    <DialogTrigger asChild>
                      <div className="border-cyber-glow shadow-cyber bg-gradient-card relative cursor-pointer overflow-hidden rounded-lg border p-3 transition-all duration-300 hover:shadow-[0_0_50px_rgba(139,92,246,0.3)] sm:rounded-xl sm:border-2 sm:p-6 md:rounded-2xl md:p-8 lg:p-12">
                        <div className="bg-cyber-glow/10 animate-pulse-glow absolute top-0 right-0 h-32 w-32 rounded-full blur-3xl sm:h-48 sm:w-48 md:h-64 md:w-64" />
                        <div className="relative z-10">
                          <div className="mb-3 flex items-center justify-center gap-1 sm:mb-4 sm:gap-2 md:mb-6 md:gap-3">
                            <div className="text-lg sm:text-2xl md:text-4xl">🕯️</div>
                            <h2 className="text-center text-lg font-bold sm:text-2xl md:text-3xl lg:text-4xl">
                              <span className="text-cyber-glow animate-cyber-glow">
                                Today's Memorial
                              </span>
                            </h2>
                            <div className="text-lg sm:text-2xl md:text-4xl">🕯️</div>
                          </div>
                          <p className="text-memorial-muted mb-3 text-center text-sm sm:mb-6 sm:text-base md:mb-8 md:text-lg">
                            Honoring the anniversary of software that departed on this day
                          </p>
                          <div className="bg-memorial-bg/50 border-memorial-border flex flex-col items-center gap-3 rounded-lg border p-3 sm:gap-6 sm:rounded-xl sm:p-4 md:gap-8 md:p-6">
                            <div className="flex-shrink-0 text-3xl sm:text-5xl md:text-8xl lg:text-9xl">
                              {item.logo}
                            </div>
                            <div className="flex-1 text-center">
                              <h3 className="text-memorial-text mb-2 text-lg font-bold sm:mb-3 sm:text-xl md:text-3xl lg:text-4xl">
                                {item.name}
                              </h3>
                              {item.description && (
                                <p className="text-memorial-muted mb-2 line-clamp-2 text-xs sm:mb-3 sm:line-clamp-3 sm:text-sm md:mb-4 md:text-base lg:text-lg">
                                  {item.description}
                                </p>
                              )}
                              <div className="flex flex-col gap-2 sm:gap-3">
                                <div className="flex flex-col gap-2 sm:gap-3 md:gap-4">
                                  <div className="bg-memorial-card border-memorial-border inline-flex items-center gap-1 rounded border px-2 py-1 text-xs sm:gap-2 sm:px-3 sm:py-2 sm:text-sm md:px-4">
                                    <span className="text-memorial-muted">Launched:</span>
                                    <span className="text-cyber-glow font-mono">
                                      {item.launchDate}
                                    </span>
                                  </div>
                                  <div className="bg-memorial-card border-memorial-border inline-flex items-center gap-1 rounded border px-2 py-1 text-xs sm:gap-2 sm:px-3 sm:py-2 sm:text-sm md:px-4">
                                    <span className="text-memorial-muted">Obsolete:</span>
                                    <span className="font-mono text-red-400">{item.endDate}</span>
                                  </div>
                                </div>
                                <div className="bg-cyber-glow/10 border-cyber-glow/30 inline-flex items-center justify-center gap-1 rounded border px-2 py-1 text-xs sm:gap-2 sm:px-3 sm:py-2 sm:text-sm md:px-4">
                                  <span className="text-memorial-muted">Served:</span>
                                  <span className="text-cyber-glow font-semibold">
                                    {calculateYearsOfService(item.launchDate, item.endDate)} years
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </DialogTrigger>
                    <SoftwareDetails software={item} />
                  </Dialog>
                </CarouselItem>
              ))}
            </CarouselContent>
            {software.length > 1 && (
              <>
                <CarouselPrevious className="bg-memorial-card border-cyber-glow/50 text-memorial-text hover:bg-memorial-border hover:text-cyber-glow left-2" />
                <CarouselNext className="bg-memorial-card border-cyber-glow/50 text-memorial-text hover:bg-memorial-border hover:text-cyber-glow right-2" />
                <div className="mt-4 flex justify-center gap-2">
                  {software.map((item, index) => (
                    <button
                      key={item.id}
                      onClick={() => api?.scrollTo(index)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        index === current
                          ? 'bg-cyber-glow w-8'
                          : 'bg-memorial-border hover:bg-memorial-muted w-2'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </Carousel>
        </div>
      </div>
    </div>
  );
};
